<template>
  <div id="content">
    <v-card v-show="$vuetify.breakpoint.mdAndUp">
      <v-responsive max-height="500">
        <v-img
          :src="banner"
          max-height="500"
          gradient="to top, rgba(255,255,255,0), rgba(200,200,200,.15), rgba(25,25,25,.7)"
        >
          <v-layout fill-height justify-center align-center>
            <h1 class="banner-title">Events</h1>
          </v-layout>
        </v-img>
      </v-responsive>
    </v-card>

    <!-- Upcoming Event -->
    <v-container :class="{ 'pa-0': $vuetify.breakpoint.xsOnly }" grid-list-lg>
      <v-layout row wrap>
        <v-flex v-for="(event, i) in currentEvents" :key="i" xs12 sm12 lg12>
          <v-layout row wrap align-center>
            <v-flex xs12 sm6 md6 lg7>
              <v-card flat :to="`/events/${event._id}`">
                <v-img
                  :src="eventCover(event)"
                  :lazy-src="require('@/assets/image-not-available-3.jpg')"
                  :aspect-ratio="16/9"
                  class="grey lighten-2"
                  @error="imageLoadError"
                ></v-img>
              </v-card>
            </v-flex>
            <v-flex xs12 sm6 md6 lg5>
              <v-card flat :to="`/events/${event._id}`">
                <h3 class="section-title pl-3 pt-3">Unraveling</h3>
                <v-card-title class="pb-0">
                  <h3
                    class="mb-2"
                    :class="$vuetify.breakpoint.smAndDown ? 'headline' : 'display-1'"
                  >{{ event.title }}</h3>
                </v-card-title>
                <v-list :dense="$vuetify.breakpoint.xsOnly">
                  <v-list-tile>
                    <v-list-tile-action>
                      <v-icon color="black">access_time</v-icon>
                    </v-list-tile-action>

                    <v-list-tile-content>
                      <v-list-tile-title>{{ eventDate(event.startDate, event.endDate) }}</v-list-tile-title>
                      <v-list-tile-sub-title>{{ eventTime(event.startDate, event.endDate) }}</v-list-tile-sub-title>
                    </v-list-tile-content>
                  </v-list-tile>

                  <v-list-tile>
                    <v-list-tile-action>
                      <v-icon color="red">place</v-icon>
                    </v-list-tile-action>

                    <v-list-tile-content>
                      <v-list-tile-title>{{ event.place }}</v-list-tile-title>
                      <v-list-tile-sub-title>{{ event.address }}</v-list-tile-sub-title>
                    </v-list-tile-content>
                  </v-list-tile>
                </v-list>
                <v-card-text v-show="$vuetify.breakpoint.lgAndUp">
                  <p class="section-title body-2">Details</p>
                  {{ event.description.substring(0,200)+"..." }}
                </v-card-text>
              </v-card>
            </v-flex>
          </v-layout>
        </v-flex>
      </v-layout>
    </v-container>

    <!-- Next Attraction -->
    <v-container grid-list-md>
      <div class="my-4">
        <h3 class="subheading font-weight-bold section-title">Next Attraction</h3>
      </div>
      <v-layout v-if="upcomingEvents.length !== 0" row wrap>
        <v-flex v-for="(event, i) in upcomingEvents" :key="i" xs12 offset-sm1 sm10 offset-lg0 lg6>
          <v-layout class="mb-4" row wrap align-center>
            <v-flex xs12 sm4 md5>
              <v-card flat :to="`/events/${event._id}`">
                <v-img
                  :src="eventCover(event)"
                  :lazy-src="require('@/assets/image-not-available-3.jpg')"
                  :aspect-ratio="16/9"
                  class="grey lighten-2"
                  @error="imageLoadError"
                ></v-img>
              </v-card>
            </v-flex>
            <v-flex xs12 sm8 md7>
              <v-card flat :to="`/events/${event._id}`">
                <v-card-title class="pb-0">
                  <h3 class="title mb-2">{{ event.title }}</h3>
                </v-card-title>
                <v-list :dense="$vuetify.breakpoint.xsOnly">
                  <v-list-tile>
                    <v-list-tile-action v-show="$vuetify.breakpoint.smAndUp">
                      <v-icon color="black">access_time</v-icon>
                    </v-list-tile-action>

                    <v-list-tile-content>
                      <v-list-tile-title>{{ eventDate(event.startDate, event.endDate) }}</v-list-tile-title>
                      <v-list-tile-sub-title>{{ eventTime(event.startDate, event.endDate) }}</v-list-tile-sub-title>
                    </v-list-tile-content>
                  </v-list-tile>

                  <v-list-tile>
                    <v-list-tile-action v-show="$vuetify.breakpoint.smAndUp">
                      <v-icon color="red">place</v-icon>
                    </v-list-tile-action>

                    <v-list-tile-content>
                      <v-list-tile-title>{{ event.place }}</v-list-tile-title>
                      <v-list-tile-sub-title>{{ event.address }}</v-list-tile-sub-title>
                    </v-list-tile-content>
                  </v-list-tile>
                </v-list>
              </v-card>
            </v-flex>
          </v-layout>
        </v-flex>
      </v-layout>
      <v-card v-else height="300" flat>
        <v-layout fill-height align-center>
          <v-flex text-xs-center>
            <v-icon x-large>cloud_off</v-icon>
            <h4 class="display-1 my-2">Unable to load next attractions</h4>
            <p class="subheading">
              Something went wrong. Try reloading the page again.
            </p>
            <v-btn color="primary" @click="getUpcomingEvents">Try Again</v-btn>
          </v-flex>
        </v-layout>
      </v-card>

      <!-- Blast from the Past -->
      <div class="my-4">
        <h3 class="subheading font-weight-bold section-title">Blast from the Past</h3>
      </div>
      <v-layout v-if="pastEvents.length !== 0" row wrap>
        <v-flex v-for="(event, i) in pastEvents" :key="i" xs12 offset-sm1 sm10 offset-lg0 lg6>
          <v-layout class="mb-4" row wrap align-center>
            <v-flex xs12 sm4 md5 lg5>
              <v-card flat :to="`/events/${event._id}`">
                <v-img
                  :src="eventCover(event)"
                  :lazy-src="require('@/assets/image-not-available-3.jpg')"
                  :aspect-ratio="16/9"
                  class="grey lighten-2"
                  @error="imageLoadError"
                ></v-img>
              </v-card>
            </v-flex>
            <v-flex xs12 sm8 md7 lg7>
              <v-card flat :to="`/events/${event._id}`">
                <v-card-title class="pb-0">
                  <h3 class="title mb-2">{{ event.title }}</h3>
                </v-card-title>
                <v-list :dense="$vuetify.breakpoint.xsOnly">
                  <v-list-tile>
                    <v-list-tile-action v-show="$vuetify.breakpoint.smAndUp">
                      <v-icon color="black">access_time</v-icon>
                    </v-list-tile-action>

                    <v-list-tile-content>
                      <v-list-tile-title>{{ eventDate(event.startDate, event.endDate) }}</v-list-tile-title>
                      <v-list-tile-sub-title>{{ eventTime(event.startDate, event.endDate) }}</v-list-tile-sub-title>
                    </v-list-tile-content>
                  </v-list-tile>

                  <v-list-tile>
                    <v-list-tile-action v-show="$vuetify.breakpoint.smAndUp">
                      <v-icon color="red">place</v-icon>
                    </v-list-tile-action>

                    <v-list-tile-content>
                      <v-list-tile-title>{{ event.place }}</v-list-tile-title>
                      <v-list-tile-sub-title>{{ event.address }}</v-list-tile-sub-title>
                    </v-list-tile-content>
                  </v-list-tile>
                </v-list>
              </v-card>
            </v-flex>
          </v-layout>
        </v-flex>
      </v-layout>
      <v-card v-else height="300" flat>
        <v-layout fill-height align-center>
          <v-flex text-xs-center>
            <v-icon x-large>cloud_off</v-icon>
            <h4 class="display-1 my-2">Unable to load past events</h4>
            <p class="subheading">
              Something went wrong. Try reloading the page again.
            </p>
            <v-btn color="primary" @click="getPastEvents">Try Again</v-btn>
          </v-flex>
        </v-layout>
      </v-card>
    </v-container>
  </div>
</template>

<script>
import ImageMixin from "@/mixins/imageMixin";
import AuthenticationService from "@/services/AuthenticationService";
export default {
  name: "Events",
  data() {
    return {
      banner: require("../assets/banner/events-banner.jpg"),
      currentEvents: [],
      loadingCurrent: false,
      currentEventsPage: 1,
      upcomingEvents: [],
      loadingUpcoming: false,
      upcomingEventsPage: 1,
      pastEvents: [],
      loadingPast: false,
      pastEventsPage: 1
    };
  },
  mounted() {
    this.getCurrentEvents();
    this.getUpcomingEvents();
    this.getPastEvents();
  },
  methods: {
    async getCurrentEvents() {
      this.loadingCurrent = true;
      const response = await AuthenticationService.getCurrentEvent();
      this.currentEvents = response.data;
      this.loadingCurrent = false;
    },
    async getUpcomingEvents() {
      this.loadingUpcoming = true;
      let limit = 6;
      const response = await AuthenticationService.getUpcomingEvents(
        limit,
        this.upcomingEventsPage++
      );
      this.upcomingEvents = this.upcomingEvents.concat(response.data);
      this.loadingUpcoming = false;
    },
    async getPastEvents() {
      this.loadingPast = true;
      let limit = 6;
      const response = await AuthenticationService.getPastEvents(
        limit,
        this.pastEventsPage++
      );
      this.pastEvents = this.pastEvents.concat(response.data);
      this.loadingPast = false;
    }
  },

  mixins: [ImageMixin]
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
.section-title {
  padding-bottom: 12px;
  color: rgba(0, 0, 0, 0.7);
  border-bottom: 1px solid #dac7a4;
}
</style>


// WEBPACK FOOTER //
// src/components/Events.vue